import { useSyncExternalStore } from "react";

type VentuzWindow = Window & {
  FrameCount?: number;
  SetFrameCount?: (frame?: number) => void;
  ventuz?: { value?: number };
  __wallFrameClock?: {
    enabled: () => boolean;
    frame: () => number;
    fps: () => number;
  };
};

const params = new URLSearchParams(window.location.search);
const configured = params.get("sync") === "ventuz" || params.get("ventuzSync") === "1";
const parsedFps = Number(params.get("fps"));
const fps = Number.isFinite(parsedFps) && parsedFps > 0 ? parsedFps : 50;
const diagnosticFrameParam = params.get("frame");
const diagnosticFrame = diagnosticFrameParam === null ? Number.NaN : Number(diagnosticFrameParam);
const browser = window as VentuzWindow;

let enabled = configured || Number.isFinite(Number(browser.FrameCount)) || Number.isFinite(diagnosticFrame);
let frame = Number.isFinite(Number(browser.FrameCount))
  ? Math.max(0, Math.trunc(Number(browser.FrameCount)))
  : Number.isFinite(diagnosticFrame)
    ? Math.max(0, Math.trunc(diagnosticFrame))
    : 0;
const listeners = new Set<() => void>();
const frameListeners = new Set<(frame: number) => void>();
// The wall's continuous motion is CSS/video-driven. React only needs to sample
// the absolute clock for state boundaries (card generation/phase, CTA, palette),
// not redraw the full 8K composition for every injected output frame.
const uiSampleFrames = Math.max(1, Math.round(fps / 2));
let lastUiBucket = -1;

const emit = () => listeners.forEach((listener) => listener());

// A Ventuz Custom Model method with this name invokes the global JavaScript
// function and passes its optional integer argument. WebBrowserJavaScript is
// therefore only needed to set an initial FrameCount before the app boots.
browser.SetFrameCount = (nextFrame?: number) => {
  const injected = Number(nextFrame ?? browser.ventuz?.value ?? browser.FrameCount);
  if (!Number.isFinite(injected)) return;
  const normalized = Math.max(0, Math.trunc(injected));
  const changed = !enabled || normalized !== frame;
  enabled = true;
  frame = normalized;
  browser.FrameCount = normalized;
  if (changed) {
    // High-frequency effects can subscribe directly without making the full
    // React wall render at the 50 fps Ventuz input rate.
    frameListeners.forEach((listener) => listener(frame));
    const uiBucket = Math.floor(frame / uiSampleFrames);
    if (uiBucket !== lastUiBucket) {
      lastUiBucket = uiBucket;
      emit();
    }
  }
};

browser.__wallFrameClock = {
  enabled: () => enabled,
  frame: () => frame,
  fps: () => fps,
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => `${enabled ? 1 : 0}:${frame}`;

export type VentuzFrameClock = {
  enabled: boolean;
  frame: number;
  fps: number;
  elapsedMs: number;
};

export const useVentuzFrameClock = (): VentuzFrameClock => {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [enabledValue, frameValue] = snapshot.split(":").map(Number);
  return {
    enabled: enabledValue === 1,
    frame: frameValue,
    fps,
    elapsedMs: Math.round(frameValue * 1000 / fps),
  };
};

export const wallSyncSeed = params.get("syncSeed") ?? params.get("layoutSeed") ?? "madonna-outernet-lockstep-v1";

export const subscribeVentuzFrames = (listener: (frame: number) => void) => {
  frameListeners.add(listener);
  return () => {
    frameListeners.delete(listener);
  };
};
